import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { createSelector } from '@ngrx/store';
import { map, switchMap, takeUntil, filter } from 'rxjs/operators';
import { BaseAsyncValue } from '../../../../shared/models/async-value';
import { SpecFieldNames } from '../../../modules/specification/constants/specification-field-names.enum';
import { getContractsBalance } from '../selectors/contracts.selectors';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { UNITS } from '@ordersnproducts/modules/shared/constants/units';
import * as moment from 'moment';
import { getOrderSelectedDetail } from '@ordersnproducts/modules/new-modify-order/selectors/order-selected-detail.selector';
import { isSameDay } from 'app/calendar-view/utils/is-same-day';
import { OrderStatusEnum } from '@cemex-core/cmx-api';
import { CapacityEnum } from '@ordersnproducts/modules/shared/constants/capacity';

const getContractsBalanceSelector = createSelector(getContractsBalance, (contractsBalance) => {
  return contractsBalance;
});

const dataCommercialProduct = createSelector(
  getOrderSelectedDetail,
  getContractsBalanceSelector,
  (order, contractsBalance) => {
    return {
      order,
      contractsBalance,
    };
  },
);

export namespace RmsMaximumCapacityProvider {
  export class RmsMaxCapacityRemaining extends BaseAsyncValue<number> {
    getValue(): Observable<number> {
      return this.store.select(getOrderSelectedDetail).pipe(
        map((order) => {
          const remaining = _safe(() => order['loadWithoutAdjustment'].quantityRemain);
          if (order && order.orderStatusCode === OrderStatusEnum.InProgressOnline && remaining !== undefined) {
            return remaining;
          } else {
            return 1;
          }
        }),
      );
    }
  }
  export class RmsMaxCapacityLoads extends BaseAsyncValue<number> {
    getValue(): Observable<number> {
      return this.store.select(getOrderSelectedDetail).pipe(
        map((order) => {
          const loads = _safe(() => order['loadWithoutAdjustment']['loadsRemain']);
          if (order && order.orderStatusCode === OrderStatusEnum.InProgressOnline && loads !== undefined) {
            return loads;
          } else {
            return 1;
          }
        }),
      );
    }
  }
  export abstract class RmsMaximumCapacityBase<T> extends BaseAsyncValue<T> {
    public unitSubject = new Subject();
    protected unboxSalesArea() {
      return this.store.select(getContractsBalanceSelector).pipe(
        map((contractsBalance) => {
          const contractCode = this.form.get(SpecFieldNames.CONTRACT_SELECT).value;
          const remaining = _safe(() => contractsBalance[contractCode][0].agreementItems[0].quantity);
          return remaining;
        }),
      );
    }
  }
  export class RmsMaxCapacity extends RmsMaximumCapacityBase<number> {
    getValue(): Observable<number> {
      return this.unitSubject.pipe(
        switchMap(() => this.unboxSalesArea()),
        map((remaining) => {
          return remaining ? Number(remaining) : CapacityEnum.RMS_MAX_CAPACITY_DEFAULT;
        }),
      );
    }
  }

  export class RmsMaxCapacityModify extends RmsMaximumCapacityBase<number> {
    getValue(): Observable<number> {
      return this.unitSubject.pipe(
        switchMap(() => this.unboxSalesArea()),
        switchMap((max) =>
          this.store.select(dataCommercialProduct).pipe(
            takeUntil(this.ngDestroy),
            filter((data) => !!data),
            map((data) => ({ data, max })),
          ),
        ),
        map(({ data, max }) => {
          const order = _safe(() => data.order);
          const plantDate = moment
            .utc(_safe(() => order.programmedDateTime))
            .local()
            .format('YYYY-MM-DD');
          const localDate = moment().format('YYYY-MM-DD');
          const isSame = isSameDay(localDate, plantDate);
          if (isSame || order.orderStatusCode === OrderStatusEnum.InProgressOnline) {
            return _safe(() => order.totalQuantity);
          } else if (max) {
            return Number(max) + order.totalQuantity;
          } else {
            return CapacityEnum.RMS_MAX_CAPACITY_DEFAULT;
          }
        }),
      );
    }
  }

  export class Message extends RmsMaximumCapacityBase<string> {
    getValue(): Observable<string> {
      const translate: TranslateService = this.injector.get(TranslateService);
      return this.unitSubject.pipe(
        switchMap(() => this.unboxSalesArea()),
        map((remaining) => {
          return remaining
            ? `${translate.instant('views.specifications.remaining_volume')}: ${remaining} ${UNITS.CUBIC_METERS}`
            : '';
        }),
      );
    }
  }

  export class MessageUsa extends RmsMaximumCapacityBase<string> {
    getValue(): Observable<string> {
      const translate: TranslateService = this.injector.get(TranslateService);
      return this.unitSubject.pipe(
        switchMap(() => this.unboxSalesArea()),
        map((remaining) => {
          return remaining
            ? `${translate.instant('views.specifications.remaining_volume')}: ${remaining} ${UNITS.CUBIC_YARD}`
            : '';
        }),
      );
    }
  }

  export class MessageModify extends RmsMaximumCapacityBase<string> {
    getValue(): Observable<string> {
      const translate: TranslateService = this.injector.get(TranslateService);
      let sameDayMax: number = null;
      return this.unitSubject.pipe(
        switchMap(() =>
          this.store.select(dataCommercialProduct).pipe(
            takeUntil(this.ngDestroy),
            filter((state) => !!state),
            switchMap((state) => {
              if (state && state.order) {
                const plantDate = moment.utc(state.order.programmedDateTime).local().format('YYYY-MM-DD');
                const localDate = moment().format('YYYY-MM-DD');
                const isSame = isSameDay(localDate, plantDate);
                if (isSame) {
                  sameDayMax = state.order.totalQuantity;
                }
              }
              return this.unboxSalesArea();
            }),
          ),
        ),
        map((max) => {
          if (sameDayMax) {
            max = sameDayMax.toString();
          }
          return max
            ? `${translate.instant('views.specifications.remaining_volume')}: ${max} ${UNITS.CUBIC_METERS}`
            : '';
        }),
      );
    }
  }

  export class MessageModifyUsa extends RmsMaximumCapacityBase<string> {
    getValue(): Observable<string> {
      const translate: TranslateService = this.injector.get(TranslateService);
      let sameDayMax: number = null;
      return this.unitSubject.pipe(
        switchMap(() =>
          this.store.select(dataCommercialProduct).pipe(
            takeUntil(this.ngDestroy),
            filter((state) => !!state),
            switchMap((state) => {
              if (state && state.order) {
                const plantDate = moment.utc(state.order.programmedDateTime).local().format('YYYY-MM-DD');
                const localDate = moment().format('YYYY-MM-DD');
                const isSame = isSameDay(localDate, plantDate);
                if (isSame) {
                  sameDayMax = state.order.totalQuantity;
                }
              }
              return this.unboxSalesArea();
            }),
          ),
        ),
        map((max) => {
          if (sameDayMax) {
            max = sameDayMax.toString();
          }
          return max ? `${translate.instant('views.specifications.remaining_volume')}: ${max} ${UNITS.CUBIC_YARD}` : '';
        }),
      );
    }
  }
}

export namespace CapacityProvider {
  export class Message extends BaseAsyncValue<string> {
    getValue(): Observable<string> {
      const translate: TranslateService = this.injector.get(TranslateService);
      const quantityControlCongif = this.config;
      const quantityValue = this.form.get(SpecFieldNames.QUANTITY).value;

      if (quantityControlCongif['max'] > quantityValue || quantityControlCongif['min'] > quantityValue) {
        return of(`${translate.instant('views.specifications.wrong_quantity')})`);
      } else {
        return of('');
      }
    }
  }

  export class MinMaxMessage extends BaseAsyncValue<string> {
    getValue(): Observable<string> {
      const translate: TranslateService = this.injector.get(TranslateService);
      const quantityControlCongif = this.config;

      if (quantityControlCongif) {
        return of(
          `${translate.instant('views.specifications.quantity_vales')}`
            .replace(' %', `${1000} ${UNITS.CUBIC_METERS}`)
            .replace('&', `${1} ${UNITS.CUBIC_METERS}`),
        );
      } else {
        return of('');
      }
    }
  }
}
